type Identifier<TValue> = (item: TValue) => string;
type Reducer<TAccumulator, TValue> = (previousValue: TAccumulator, currentValue: TValue, currentIndex: number, array: TValue[]) => TAccumulator;
type Map<TValue, TMap> = (value: TValue, index: number, array: TValue[]) => TMap;

interface IRule<T> {

	/** Tests the given item to see if it matches the rule */
	predicate: (item: T) => boolean;

	/** Sort run after all the matched items are collected */
	sort?: (a: T, b: T) => number;
}

const joiner = <T>(fn: Identifier<T>): Reducer<string, T> => (t, v) => `${t}${fn(v)}`;
const defaultIdentifier: Identifier<any> = item => typeof item === "string" ? item : (item || "").toString();

/**
 * Compares two arrays to check if they have differences.
 * @param a One of the two lists to compare against each other.
 * @param b One of the two lists to compare against each other.
 * @param identifier Function runs over each element in the array. Returns a uniquely identifiable string to be used during comparison. Will default to a toString comparison, if A and B are string arrays it will default to comparing the string values directly.
 * @returns TRUE if the lists are different, FALSE if the list are the same.
 */
export const hasDifference = <T>(a: T[] = [], b: T[] = [], identifier: Identifier<T> = defaultIdentifier): boolean => 	
	a.length === 0 && b.length === 0 ? false :
	a.length !== b.length ? true :
	a.reduce(joiner(identifier), "") !== b.reduce(joiner(identifier), "");

/**
 * Unzips an array.
 * @param array The array to unzip.
 * @param unzip the unzipper callback, data returned in index 1 and 2 will come out as members of the returned arrays index 1 and 2 respectively.
 * @returns an object containing an array of the unzipped lists.
 */
export const unzip = <T, U, V>(array:T[] = [], unzip: Map<T, [U, V]> = () => [null, null]): [U[], V[]] => {

	const unzipped: [U[], V[]] = [ [], [] ];
	
	array.forEach((v, i, a) => {
		const [ item1, item2 ] = unzip(v, i, a);
		unzipped[0].push(item1);
		unzipped[1].push(item2);
	});

	return unzipped;
}

/**
 * Returns the first matching item. Otherwise defaultValue. * 
 * @param array The array to search
 * @param predicate The search predicate function
 * @param defaultValue The default value to return if not found
 */
export const firstOrDefault = <T>(array:T[], predicate: (T) => boolean = null, defaultValue: T = null) => {
	if (array && Array.isArray(array))
		for (let i = 0; i < array.length; i++)
			if (!predicate || predicate(array[i]))
				return array[i];

	return defaultValue;
}

/**
 * Returns the last matching item. Otherwise defaultValue. * 
 * @param array The array to search
 * @param predicate The search predicate function
 * @param defaultValue The default value to return if not found
 */
export const lastOrDefault = <T>(array:T[], predicate: (T) => boolean = null, defaultValue: T = null) => {
	if (array && Array.isArray(array))
		for (let i = array.length - 1; i >= 0; i--)
			if (!predicate || predicate(array[i]))
				return array[i];

	return defaultValue;
}

/**
 * For a given list of item, each item will be matched against a rule.
 * The item's position in the output list is determined by which rule it matches.
 * An items that didn't match a rule will be at the buttom of the output list.
 * @param list list of items to run the rules against
 * @param rules List of rules to match against. Rule order matters, each list item will only match a maximum of a single rule
 */
export const cascadingSort = <T>(list: T[], rules: IRule<T>[]): T[] => {

	const ruleMatches: T[][] = rules.map(() => []);
	const remaining: T[] = [];

	list.forEach(item =>
		!!rules.find((rule, i) => rule.predicate(item) && !!ruleMatches[i].push(item))
		|| remaining.push(item)
	);

	return ruleMatches.reduce((t, matchGroup, i) => 
		t.concat(rules[i].sort ? matchGroup.sort(rules[i].sort) : matchGroup)
	, []).concat(remaining);
};

/**
 * Returns a new array with the distinct/unique elements from the source array
 * @param array array to select the distinct elements from.
 */
export function distinct<T>(array: T[]): Array<T> {
	return [...new Set(array)];
}

/**
 * Chops x elements off the tail of the array
 * @param list 
 * @param x 
 */
export function chopRight<T>(array: T[], x: number) {
	if (!array)
		return [];

	const keep = array.length - x;
	if (keep < 0)
		return [];

	return array.slice(0, keep);
}

/**
 * Returns the right x elements. If x < 0 x is interpreted as 0 - (-1 * x)
 * @param list 
 * @param x 
 */
export function right<T>(array: T[], x: number) {
	if (!array)
		return [];

	let start;

	if (x < 0) {
		start = (x * -1);
	} else {
		start = array.length - x;
	}

	if (start < array.length)
		return array.slice(start, array.length);

	return [];
}

/**
 * Chops x elements off the start of the array
 * @param list 
 * @param x 
 */
export function chopLeft<T>(array: T[], x: number) {
	if (!array)
		return [];

	if (x <= 0)
		return array;

	if (x >= array.length)
		return [];

	return array.slice(x);
}

/**
 * Returns the left x elements. If x < 0 x is interpreted as 0 - (-1 * x)
 * @param list 
 * @param x 
 */
export function left<T>(array: T[], x: number) {
	if (!array || x <= 0)
		return [];

	if (x > array.length)
		return array;

	return array.slice(0, x);
}